import { useState } from "react";
import { Form, Formik } from "formik";

import { useMaterialUIController } from "context";

import {
  Alert,
  Box,
  colors,
  Divider,
  Grid,
  Icon,
  Modal,
  Typography,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDAvatar from "components/MDAvatar";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import FormField from "layouts/pages/users/new-user/components/FormField";

import { IListInscricoesActive } from "interfaces/inscricoes";
import form from "../schemas/forms";

import { initialValuesCortesia } from "../schemas/initialValues";
import { validationCortesia } from "../schemas/validations";
import { formatCurrency } from "helpers/utils";

const {
  formField: { observacao },
} = form;

interface Props {
  open: boolean;
  handleOpen: () => void;
  handleSubmit: (values: any) => void;
  inscricao: IListInscricoesActive;
}

export default function ModalBaixaManualPay({
  open,
  handleOpen,
  handleSubmit,
  inscricao,
}: Props) {
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const [isConfimed, setIsConfimed] = useState<boolean>(false);

  const NUM_ATLETAS_INSCRITOS = inscricao.inscritos.length || 0;

  // Salvar cortesia da inscrição
  const handleSaveCortesia = async (values: any) => {
    handleSubmit(values);
  };

  return (
    <Modal
      open={open}
      onClose={handleOpen}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box
        sx={{
          ...style,
          width: { xs: "95%", sm: 400, md: 800 },
          p: 3,
          position: "relative",
        }}
      >
        <MDBox>
          <MDTypography variant="h5">
            Definir baixa manual de pagamento
          </MDTypography>
          <MDTypography variant="subtitle2" color="text">
            {inscricao.evento.titulo}
          </MDTypography>
        </MDBox>

        <Divider />

        {/* DADOS DO ATLETA */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={5}>
            <MDBox>
              <MDTypography variant="body2" color="info" fontWeight="bold">
                {inscricao.nomeResponsavel}
              </MDTypography>
              <MDTypography variant="body2" color="text">
                CPF: {inscricao.cpfResponsavel}
              </MDTypography>
            </MDBox>
          </Grid>

          <Grid item xs={12} sm={4}>
            <MDBox>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Número de Inscrição
              </MDTypography>
              <MDTypography
                variant="body2"
                color="text"
                bgcolor=""
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                }}
              >
                #{inscricao.numeroInscricao}
              </MDTypography>
            </MDBox>
          </Grid>

          <Grid item xs={12} sm={3}>
            <MDBox>
              <MDTypography
                variant="caption"
                color="text"
                fontWeight="bold"
              ></MDTypography>
              <MDTypography
                variant="body1"
                color="text"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                }}
              >
                {formatCurrency(inscricao.totalInscricao)}
              </MDTypography>
              <img
                style={{ width: "100px", height: "50px" }}
                src={inscricao.pagamentos.tipoPagamento.icon}
                alt={inscricao.pagamentos.tipoPagamento.tipoPagamento}
              />
            </MDBox>
          </Grid>
        </Grid>
        <Alert variant="standard" severity="warning" sx={{ mt: 2 }}>
          {`🏃‍♂️‍➡️ Inscrição contém ${NUM_ATLETAS_INSCRITOS} ${
            NUM_ATLETAS_INSCRITOS > 1 ? "atletas inscritos" : "atleta inscrito"
          }`}
        </Alert>
        <Divider />

        {/* Camiseta */}
        <MDBox mt={2}>
          <Formik
            initialValues={initialValuesCortesia}
            validationSchema={validationCortesia}
            onSubmit={(values) => handleSaveCortesia(values)}
          >
            {({ values, isValid, handleChange }) => {
              return (
                <Form id="form-cortesia">
                  <MDTypography variant="body2" color="text" sx={{ mb: 2 }}>
                    Para efeturar a baixa do pagamento da inscrição é necessário
                    informar o motivo.
                  </MDTypography>
                  <FormField
                    required
                    multiline
                    rows={4}
                    type={observacao.type}
                    name={observacao.name}
                    label={observacao.label}
                    variant="outlined"
                    value={values.observacao}
                    onChange={handleChange}
                  />
                  {/* AÇÕES */}
                  {values.observacao !== "" && !isConfimed && (
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      bgColor={colors.blue[800]}
                      borderRadius="md"
                      color="white"
                      p={2}
                      sx={{ width: "100%" }}
                    >
                      <Typography
                        variant="body2"
                        color="text"
                        display="flex"
                        alignItems="center"
                      >
                        <Icon fontSize="large">warning</Icon> Você tem certeza
                        de que deseja confirmar a baixa do pagamento da
                        inscrição?
                      </Typography>
                      <MDButton
                        variant="contained"
                        color="light"
                        onClick={() => setIsConfimed(true)}
                      >
                        Sim
                      </MDButton>
                    </MDBox>
                  )}

                  <Divider />
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="end"
                    mt={3}
                    gap={2}
                  >
                    {isConfimed && (
                      <MDBox
                        display="flex"
                        alignItems="center"
                        flexDirection="row"
                        gap={1}
                      >
                        <MDAvatar src={user.avatar} alt={user.nome} size="md" />
                        <MDBox display="flex" flexDirection="column">
                          <Typography variant="caption" color="text">
                            Usuário responsável pela baixa
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text"
                            fontWeight="bold"
                          >
                            {user.nome}
                          </Typography>
                        </MDBox>
                      </MDBox>
                    )}
                    <MDBox
                      display="flex"
                      flexDirection="row"
                      gap={1}
                      justifyContent="flex-end"
                    >
                      {isConfimed && (
                        <MDButton
                          variant="gradient"
                          color="info"
                          type="submit"
                          disabled={!isValid}
                          title="dfsdf"
                        >
                          Confirmar baixa
                        </MDButton>
                      )}
                      <MDButton
                        variant="gradient"
                        color="light"
                        onClick={handleOpen}
                      >
                        Cancelar
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </Form>
              );
            }}
          </Formik>
        </MDBox>
      </Box>
    </Modal>
  );
}

const style = {
  position: "absolute" as "absolute",
  borderRadius: "5px",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
};
